import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import useCreate from "../../hooks/useCreate";
import useDelete from "../../hooks/useDelete";
import useGet from "../../hooks/useGet";
import useUpdate from "../../hooks/useUpdate";
import API_BASE_URL from "../../config/api";
import { useAuth } from "../../pages/AuthProvider";

const emptyAttribute = { heading: "", valuesText: "" };

function AddCategory() {
  const navigate = useNavigate();
  const { canAccess } = useAuth();
  const { create } = useCreate("/api/categories");
  const { update } = useUpdate("/api/categories");
  const { remove } = useDelete("/api/categories");
  const { data: categoriesData, loading: categoriesLoading, mutate } = useGet("/api/categories?all=true");

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [viewCategory, setViewCategory] = useState(null);
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState("");
  const [bannerImageDesktop, setBannerImageDesktop] = useState(null);
  const [bannerPreviewDesktop, setBannerPreviewDesktop] = useState("");
  const [bannerImageMobile, setBannerImageMobile] = useState(null);
  const [bannerPreviewMobile, setBannerPreviewMobile] = useState("");
  const [formData, setFormData] = useState({
    categoryName: "",
    status: "active",
    description: "",
    metaTitle: "",
    metaDescription: "",
    goldMakingCharges: "",
    platiniumMakingCharges: "",
    attributes: [{ ...emptyAttribute }]
  });

  useEffect(() => {
    return () => {
      if (preview && preview.startsWith("blob:")) {
        URL.revokeObjectURL(preview);
      }
      if (bannerPreviewDesktop && bannerPreviewDesktop.startsWith("blob:")) {
        URL.revokeObjectURL(bannerPreviewDesktop);
      }
      if (bannerPreviewMobile && bannerPreviewMobile.startsWith("blob:")) {
        URL.revokeObjectURL(bannerPreviewMobile);
      }
    };
  }, [preview, bannerPreviewDesktop, bannerPreviewMobile]);

  const categories = useMemo(() => {
    const list = Array.isArray(categoriesData)
      ? categoriesData
      : Array.isArray(categoriesData?.categories)
      ? categoriesData.categories
      : Array.isArray(categoriesData?.data)
      ? categoriesData.data
      : [];
    return list.filter((item) => !item?.parentCategory && !item?.parentId);
  }, [categoriesData]);

  const generateSlug = (name) =>
    name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");

  const resolveImageUrl = (item) => {
    const raw = item?.image?.url || item?.image?.path || item?.imageUrl || item?.thumbnail || item?.photo || item?.image;
    if (!raw || typeof raw !== "string") return "";
    if (/^(https?:|blob:|data:)/i.test(raw)) return raw;
    const normalized = raw.replace(/^\/+/, "");
    if (normalized.startsWith("uploads/")) return `${API_BASE_URL}/${normalized}`;
    if (raw.startsWith("/")) return `${API_BASE_URL}${raw}`;
    return `${API_BASE_URL}/uploads/${normalized}`;
  };

  const resolveBannerDesktopUrl = (item) => {
    const raw =
      item?.bannerImageDesktop?.url ||
      item?.bannerImageDesktop?.path ||
      item?.bannerImageDesktopUrl ||
      item?.bannerImageDesktop;
    if (!raw || typeof raw !== "string") return "";
    if (/^(https?:|blob:|data:)/i.test(raw)) return raw;
    const normalized = raw.replace(/^\/+/, "");
    if (normalized.startsWith("uploads/")) return `${API_BASE_URL}/${normalized}`;
    if (raw.startsWith("/")) return `${API_BASE_URL}${raw}`;
    return `${API_BASE_URL}/uploads/${normalized}`;
  };

  const resolveBannerMobileUrl = (item) => {
    const raw =
      item?.bannerImageMobile?.url ||
      item?.bannerImageMobile?.path ||
      item?.bannerImageMobileUrl ||
      item?.bannerImageMobile;
    if (!raw || typeof raw !== "string") return "";
    if (/^(https?:|blob:|data:)/i.test(raw)) return raw;
    const normalized = raw.replace(/^\/+/, "");
    if (normalized.startsWith("uploads/")) return `${API_BASE_URL}/${normalized}`;
    if (raw.startsWith("/")) return `${API_BASE_URL}${raw}`;
    return `${API_BASE_URL}/uploads/${normalized}`;
  };

  const resetForm = () => {
    setFormData({
      categoryName: "",
      status: "active",
      description: "",
      metaTitle: "",
      metaDescription: "",
      goldMakingCharges: "",
      platiniumMakingCharges: "",
      attributes: [{ ...emptyAttribute }]
    });
    setImage(null);
    setPreview("");
    setBannerImageDesktop(null);
    setBannerPreviewDesktop("");
    setBannerImageMobile(null);
    setBannerPreviewMobile("");
    setEditingId("");
  };

  const openAddModal = () => {
    resetForm();
    setIsFormOpen(true);
  };

  const openEditModal = (category) => {
    const attributes =
      Array.isArray(category.attributes) && category.attributes.length
        ? category.attributes.map((attr) => ({
            heading: attr.heading || "",
            valuesText: Array.isArray(attr.values) ? attr.values.join(", ") : ""
          }))
        : [{ ...emptyAttribute }];

    setEditingId(category._id || category.id || "");
    setFormData({
      categoryName: category.name || "",
      status: category.isActive ? "active" : "inactive",
      description: category.description || "",
      metaTitle: category.metaTitle || "",
      metaDescription: category.metaDescription || "",
      goldMakingCharges: category.goldMakingCharges ?? "",
      platiniumMakingCharges: category.platiniumMakingCharges ?? "",
      attributes
    });
    setImage(null);
    setPreview(resolveImageUrl(category));
    setBannerImageDesktop(null);
    setBannerPreviewDesktop(resolveBannerDesktopUrl(category));
    setBannerImageMobile(null);
    setBannerPreviewMobile(resolveBannerMobileUrl(category));
    setIsFormOpen(true);
  };

  const openViewModal = (category) => {
    setViewCategory(category);
    setIsViewOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAttributeChange = (index, field, value) => {
    setFormData((prev) => {
      const next = [...prev.attributes];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, attributes: next };
    });
  };

  const addAttributeRow = () => {
    setFormData((prev) => ({
      ...prev,
      attributes: [...prev.attributes, { ...emptyAttribute }]
    }));
  };

  const removeAttributeRow = (index) => {
    setFormData((prev) => {
      const next = prev.attributes.filter((_, i) => i !== index);
      return { ...prev, attributes: next.length ? next : [{ ...emptyAttribute }] };
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }
    const blobUrl = URL.createObjectURL(file);
    setImage(file);
    setPreview(blobUrl);
  };

  const handleBannerDesktopChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (bannerPreviewDesktop && bannerPreviewDesktop.startsWith("blob:")) {
      URL.revokeObjectURL(bannerPreviewDesktop);
    }
    const blobUrl = URL.createObjectURL(file);
    setBannerImageDesktop(file);
    setBannerPreviewDesktop(blobUrl);
  };

  const handleBannerMobileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (bannerPreviewMobile && bannerPreviewMobile.startsWith("blob:")) {
      URL.revokeObjectURL(bannerPreviewMobile);
    }
    const blobUrl = URL.createObjectURL(file);
    setBannerImageMobile(file);
    setBannerPreviewMobile(blobUrl);
  };

  const getPreparedAttributes = () =>
    formData.attributes
      .map((attr) => ({
        heading: attr.heading.trim(),
        values: attr.valuesText
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean)
      }))
      .filter((attr) => attr.heading && attr.values.length);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage({ type: "", text: "" });
    try {
      if (!formData.categoryName.trim()) {
        setMessage({ type: "error", text: "Category name is required." });
        return;
      }

      const data = new FormData();
      data.append("name", formData.categoryName.trim());
      data.append("slug", generateSlug(formData.categoryName));
      data.append("description", formData.description || "");
      data.append("metaTitle", formData.metaTitle || "");
      data.append("metaDescription", formData.metaDescription || "");
      data.append("isActive", formData.status === "active");

      if (formData.goldMakingCharges !== "") data.append("goldMakingCharges", String(formData.goldMakingCharges));
      if (formData.platiniumMakingCharges !== "") data.append("platiniumMakingCharges", String(formData.platiniumMakingCharges));

      const preparedAttributes = getPreparedAttributes();
      if (preparedAttributes.length) data.append("attributes", JSON.stringify(preparedAttributes));
      if (image) data.append("image", image);
      if (bannerImageDesktop) data.append("bannerImageDesktop", bannerImageDesktop);
      if (bannerImageMobile) data.append("bannerImageMobile", bannerImageMobile);

      if (editingId) {
        await update(editingId, data);
        setMessage({ type: "success", text: "Category updated successfully." });
      } else {
        await create(data);
        setMessage({ type: "success", text: "Category created successfully." });
      }
      await mutate();
      setIsFormOpen(false);
      resetForm();
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || "Failed to save category.";
      setMessage({ type: "error", text: errorMsg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (categoryId) => {
    if (!categoryId) return;
    if (!window.confirm("Delete this category?")) return;
    try {
      await remove(categoryId);
      setMessage({ type: "success", text: "Category deleted successfully." });
      await mutate();
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || "Failed to delete category.";
      setMessage({ type: "error", text: errorMsg });
    }
  };

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          border: "1px solid #e2e8f0",
          borderRadius: 3,
          background: "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)"
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Category Manager
            </Typography>
            <Typography color="text.secondary">Top-level categories with quick access to subcategories and tags.</Typography>
          </Box>
          {canAccess('categories', 'create') && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAddModal}>
              Add Category
            </Button>
          )}
        </Box>
      </Paper>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          {message.text ? (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.text}
            </Alert>
          ) : null}

          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                  <TableCell>#</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Attributes</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categoriesLoading ? (
                  <TableRow>
                    <TableCell colSpan={7}>Loading categories...</TableCell>
                  </TableRow>
                ) : categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>No categories found.</TableCell>
                  </TableRow>
                ) : (
                  categories.map((category, index) => {
                    const categoryId = category._id || category.id;
                    return (
                      <TableRow key={categoryId || `${category.name}-${index}`} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{category.name || "-"}</TableCell>
                        <TableCell>{category.slug || "-"}</TableCell>
                        <TableCell>
                          <Chip label={category.isActive ? "Active" : "Inactive"} size="small" color={category.isActive ? "success" : "default"} />
                        </TableCell>
                        <TableCell>{Array.isArray(category.attributes) ? category.attributes.length : 0}</TableCell>
                        <TableCell sx={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {category.description || "-"}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Subcategories">
                            <span>
                              <IconButton
                                color="info"
                                disabled={!categoryId}
                                onClick={() => navigate(`/categories/${categoryId}/subcategories`)}
                              >
                                <SubdirectoryArrowRightIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Tags">
                            <span>
                              <IconButton
                                color="warning"
                                disabled={!categoryId}
                                onClick={() => navigate(`/categories/${categoryId}/tags`)}
                              >
                                <SellOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="View">
                            <IconButton color="primary" onClick={() => openViewModal(category)}>
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canAccess('categories', 'edit') && (
                            <Tooltip title="Edit">
                              <IconButton color="secondary" onClick={() => openEditModal(category)}>
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canAccess('categories', 'delete') && (
                            <Tooltip title="Delete">
                              <IconButton color="error" onClick={() => handleDelete(categoryId)}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={isFormOpen} onClose={() => setIsFormOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editingId ? "Edit Category" : "Create Category"}</DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="category-form" onSubmit={handleSubmit} sx={{ display: "grid", gap: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Category Name" name="categoryName" value={formData.categoryName} onChange={handleChange} required />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Meta Title"
                  name="metaTitle"
                  value={formData.metaTitle}
                  onChange={handleChange}
                  inputProps={{ maxLength: 60 }}
                  helperText={`${formData.metaTitle.length}/60`}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Meta Description"
                  name="metaDescription"
                  value={formData.metaDescription}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField select fullWidth label="Status" name="status" value={formData.status} onChange={handleChange}>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Gold Making Charges"
                  name="goldMakingCharges"
                  type="number"
                  value={formData.goldMakingCharges}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Platinium Making Charges"
                  name="platiniumMakingCharges"
                  type="number"
                  value={formData.platiniumMakingCharges}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button variant="outlined" component="label" fullWidth sx={{ height: "56px" }}>
                  Upload Image
                  <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                </Button>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button variant="outlined" component="label" fullWidth sx={{ height: "56px" }}>
                  Upload Banner (Desktop)
                  <input type="file" hidden accept="image/*" onChange={handleBannerDesktopChange} />
                </Button>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button variant="outlined" component="label" fullWidth sx={{ height: "56px" }}>
                  Upload Banner (Mobile)
                  <input type="file" hidden accept="image/*" onChange={handleBannerMobileChange} />
                </Button>
              </Grid>
            </Grid>

            {preview ? (
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <img src={preview} alt="preview" style={{ width: 76, height: 76, borderRadius: 10, objectFit: "cover" }} />
              </Box>
            ) : null}

            {bannerPreviewDesktop ? (
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <img
                  src={bannerPreviewDesktop}
                  alt="banner desktop preview"
                  style={{ width: 160, height: 76, borderRadius: 10, objectFit: "cover" }}
                />
              </Box>
            ) : null}

            {bannerPreviewMobile ? (
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <img
                  src={bannerPreviewMobile}
                  alt="banner mobile preview"
                  style={{ width: 120, height: 76, borderRadius: 10, objectFit: "cover" }}
                />
              </Box>
            ) : null}

            <Divider />

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Attributes
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton color="primary" onClick={addAttributeRow}>
                  <AddCircleOutlineIcon />
                </IconButton>
                <IconButton
                  color="error"
                  onClick={() => removeAttributeRow(formData.attributes.length - 1)}
                  disabled={formData.attributes.length === 1}
                >
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ display: "grid", gap: 1.5 }}>
              {formData.attributes.map((attr, index) => (
                <Grid container spacing={1.5} key={`attr-${index}`}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label={`Heading ${index + 1}`}
                      value={attr.heading}
                      onChange={(e) => handleAttributeChange(index, "heading", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      label={`Values ${index + 1}`}
                      placeholder="Comma separated values"
                      value={attr.valuesText}
                      onChange={(e) => handleAttributeChange(index, "valuesText", e.target.value)}
                    />
                  </Grid>
                </Grid>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setIsFormOpen(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button type="submit" form="category-form" variant="contained" disabled={submitting}>
            {submitting ? "Saving..." : editingId ? "Update Category" : "Save Category"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isViewOpen} onClose={() => setIsViewOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Category Details</DialogTitle>
        <DialogContent dividers>
          {viewCategory ? (
            <Box sx={{ display: "grid", gap: 2 }}>
              <Box
                sx={{
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  p: 2,
                  background: "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)"
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <Box
                      sx={{
                        width: { xs: "100%", md: 140 },
                        height: 140,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid #cbd5e1",
                        backgroundColor: "#fff"
                      }}
                    >
                      {resolveImageUrl(viewCategory) ? (
                        <img
                          src={resolveImageUrl(viewCategory)}
                          alt={viewCategory.name || "Category"}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <Box sx={{ height: "100%", display: "grid", placeItems: "center" }}>
                          <Typography variant="caption" color="text.secondary">
                            No image
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={9}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {viewCategory.name || "-"}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                      <Chip size="small" label={viewCategory.isActive ? "Active" : "Inactive"} color={viewCategory.isActive ? "success" : "default"} />
                      <Chip size="small" variant="outlined" label={`Slug: ${viewCategory.slug || "-"}`} />
                      <Chip size="small" variant="outlined" label={`Attributes: ${Array.isArray(viewCategory.attributes) ? viewCategory.attributes.length : 0}`} />
                    </Box>
                    <Typography color="text.secondary" sx={{ mt: 1.5 }}>
                      {viewCategory.description || "No description available."}
                    </Typography>
                    {viewCategory.metaTitle ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Meta Title: {viewCategory.metaTitle}
                      </Typography>
                    ) : null}
                  </Grid>
                </Grid>
              </Box>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Banner Images
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Desktop
                    </Typography>
                    <Box
                      sx={{
                        width: 260,
                        height: 160,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid #e2e8f0",
                        backgroundColor: "#fff"
                      }}
                    >
                      {resolveBannerDesktopUrl(viewCategory) ? (
                        <img
                          src={resolveBannerDesktopUrl(viewCategory)}
                          alt={`${viewCategory.name || "Category"} banner desktop`}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <Box sx={{ height: "100%", display: "grid", placeItems: "center" }}>
                          <Typography variant="caption" color="text.secondary">
                            No desktop banner
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Mobile
                    </Typography>
                    <Box
                      sx={{
                        width: 180,
                        height: 160,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid #e2e8f0",
                        backgroundColor: "#fff"
                      }}
                    >
                      {resolveBannerMobileUrl(viewCategory) ? (
                        <img
                          src={resolveBannerMobileUrl(viewCategory)}
                          alt={`${viewCategory.name || "Category"} banner mobile`}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <Box sx={{ height: "100%", display: "grid", placeItems: "center" }}>
                          <Typography variant="caption" color="text.secondary">
                            No mobile banner
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Paper>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Gold Making Charges
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 700 }}>
                      {viewCategory.goldMakingCharges ?? "-"}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Platinium Making Charges
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 700 }}>
                      {viewCategory.platiniumMakingCharges ?? "-"}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Attributes
                </Typography>
                {Array.isArray(viewCategory.attributes) && viewCategory.attributes.length ? (
                  <Box sx={{ display: "grid", gap: 1 }}>
                    {viewCategory.attributes.map((attr, idx) => (
                      <Box key={`view-attr-${idx}`} sx={{ display: "grid", gap: 0.5 }}>
                        <Typography sx={{ fontWeight: 600 }}>
                          {idx + 1}. {attr.heading || "-"}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          {Array.isArray(attr.values) && attr.values.length ? (
                            attr.values.map((value, valueIndex) => (
                              <Chip key={`view-attr-${idx}-value-${valueIndex}`} size="small" label={value} />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No values
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography color="text.secondary">No attributes configured.</Typography>
                )}
              </Paper>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AddCategory;
